/**
 * 图层结构编辑：复制图层、图层编组（DESIGN.md §6.1 补充）。
 *
 * 复制图层以"当前选中图层"（`AppStore.activeLayerId`）为目标。图层编组
 * 支持图层面板的多选（`AppStore.selectedLayerIds`）：传了 `memberIds` 就编
 * 那一组，省略时退回单选行为（把 `activeLayerId` 单独装进一个新组），
 * 对应真实 Photoshop 里选中单个图层按 Ctrl+G 的行为。多选编组只接受
 * **同一父组下的兄弟图层**——跨组多选编组在真实 PS 里也不是"编组"这个
 * 操作能处理的语义（那需要先决定新组该挂在哪层，超出这个命令的范围），
 * 遇到就返回 false 让调用方如实提示，不做隐晦的"挑一个父组硬编"。
 *
 * 复制图层需要连带复制像素：图层结构可以整体复制（浅拷贝），但像素只归
 * TileStore 管（CLAUDE.md 规则 6），复制结构不会自动复制像素——必须显式
 * 把源图层名下的块搬一份到新图层名下，组类图层还要递归到每个子孙。
 */
import { findLayer } from '../document/draft';
import type { Document, GroupLayer, Layer, RasterMask } from '../document/types';
import { layerId, tileKey, type LayerId } from '../engine/tile/key';
import type { TileStore } from '../engine/tile/store';
import {
  createAddLayerCommand,
  createGroupLayersCommand,
  createRemoveLayerCommand,
} from './commands/layer-commands';
import { createAddMaskCommand, maskLayerIdFor, writeMaskFromSelection } from './commands/mask';
import type { AppStore } from './store';

export interface LayerEditDeps {
  readonly tiles: TileStore;
  readonly nextLayerId: () => string;
}

/** 把 `sourceId` 名下已分配的块（各 LOD）原样复制一份到 `targetId` 名下。 */
function copyLayerPixels(tiles: TileStore, sourceId: LayerId, targetId: LayerId): void {
  for (const tile of tiles.residentTilesOf(sourceId)) {
    if (tile.data === null) continue;
    tiles.set(tileKey(targetId, tile.key.tx, tile.key.ty, tile.key.lod), tile.data);
  }
}

function cloneMask(mask: RasterMask, tiles: TileStore, newOwnerId: LayerId): RasterMask {
  const newMaskId = maskLayerIdFor(newOwnerId);
  copyLayerPixels(tiles, mask.tiles.layer, newMaskId);
  return { ...mask, tiles: tiles.handle(newMaskId) };
}

/** 深拷贝一个图层子树，每个节点（含蒙版）都换成全新 id，像素一并复制。 */
function cloneLayer(layer: Layer, tiles: TileStore, nextLayerId: () => string): Layer {
  const newId = layerId(nextLayerId());
  const mask = layer.mask === undefined ? {} : { mask: cloneMask(layer.mask, tiles, newId) };

  if (layer.kind === 'group') {
    return {
      ...layer,
      ...mask,
      id: newId,
      children: layer.children.map((child) => cloneLayer(child, tiles, nextLayerId)),
    };
  }
  if (layer.kind === 'raster') {
    copyLayerPixels(tiles, layer.id, newId);
    return { ...layer, ...mask, id: newId, tiles: tiles.handle(newId) };
  }
  if (layer.kind === 'smartObject') {
    copyLayerPixels(tiles, layer.raster.layer, newId);
    const cache =
      layer.cache === undefined
        ? {}
        : (() => {
            const cacheId = layerId(`${newId}#cache`);
            copyLayerPixels(tiles, layer.cache!.layer, cacheId);
            return { cache: tiles.handle(cacheId) };
          })();
    return { ...layer, ...mask, ...cache, id: newId, raster: tiles.handle(newId) };
  }
  // text / shape / adjustment / fill：没有 TileHandle 字段，浅拷贝即可
  return { ...layer, ...mask, id: newId };
}

/** 从文档里找到某个图层的父组 id（顶层图层的父组即根组，用 `null` 表示）。 */
function parentOf(document: Document, id: LayerId): LayerId | null {
  const location = findLayer(document.root, id);
  return location === null ? null : (location.ancestors.at(-1)?.id ?? null);
}

/**
 * 复制当前选中图层，插在它正上方并设为新的活动图层（PS 惯例）。
 * 一次调用 = 一条历史。返回是否有修改发生（无文档/无选中图层时 false）。
 */
export function duplicateActiveLayer(store: AppStore, deps: LayerEditDeps): boolean {
  const document = store.document;
  const activeId = store.activeLayerId;
  if (document === null || activeId === null) return false;
  const location = findLayer(document.root, activeId);
  if (location === null) return false;

  const parentId = location.ancestors.at(-1)?.id ?? null;
  const cloned = cloneLayer(location.layer, deps.tiles, deps.nextLayerId);
  store.dispatch(createAddLayerCommand({ layer: cloned, parentId, index: location.index + 1 }));
  store.setActiveLayer(cloned.id);
  return true;
}

/**
 * 一组图层 id 全部移出文档之后，其中最上面那个原来的位置该由谁接手——
 * 编组（这些图层被塞进一个新组）与合并图层（这些图层被合成一个新图层）
 * 用的是同一个位置算法：新节点落在原来"最上面那个成员"所在的相对位置，
 * 不是随便插在末尾。要求这些 id 全部存在且是同一父组下的兄弟图层，否则
 * 返回 null（跨父组的多选编组/合并在真实 PS 里也不是这两个操作能处理的
 * 语义——那需要先决定新节点该挂在哪层，超出这两个命令的范围）。
 */
export function computeTopmostInsertPosition(
  document: Document,
  ids: readonly LayerId[],
): { readonly parentId: LayerId | null; readonly index: number } | null {
  if (ids.length === 0) return null;
  const locations = ids.map((id) => findLayer(document.root, id));
  if (locations.some((l) => l === null)) return null; // 有 id 已经不在文档里了

  const parentIds = new Set(ids.map((id) => parentOf(document, id)));
  if (parentIds.size > 1) return null; // 不是同一父组下的兄弟图层

  const parentId = [...parentIds][0] ?? null;
  const indices = locations.map((l) => l!.index).sort((a, b) => a - b);
  const topIndex = indices[indices.length - 1]!;
  // children[0] 在最底下（index 越大越靠上）——把其余成员从列表里摘掉之后，
  // 原来在 topIndex 上面的那些成员（更大的 index）跟它的相对位置不变，
  // 但排在它下面、同样被摘掉的成员（更小的 index）会让它的下标整体减小
  const removedBelow = indices.filter((i) => i < topIndex).length;
  return { parentId, index: topIndex - removedBelow };
}

/**
 * 把选中的图层（单选或多选）装进一个新组，组落在这些图层里最上面那个的
 * 位置，新组设为活动图层。一次调用 = 一条历史（`createGroupLayersCommand`
 * 自带 invert，一步撤销）。
 */
export function groupActiveLayer(
  store: AppStore,
  deps: LayerEditDeps,
  memberIds?: readonly LayerId[],
): boolean {
  const document = store.document;
  if (document === null) return false;
  const activeId = store.activeLayerId;
  const ids = memberIds !== undefined && memberIds.length > 0 ? memberIds : activeId === null ? [] : [activeId];
  if (ids.length === 0) return false;

  const position = computeTopmostInsertPosition(document, ids);
  if (position === null) return false;
  const { parentId, index: insertIndex } = position;

  const groupId = layerId(deps.nextLayerId());
  const group: GroupLayer = {
    id: groupId,
    name: '组 1',
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: 'normal',
    clipped: false,
    locks: { all: false, transparency: false, position: false },
    transform: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
    effects: [],
    kind: 'group',
    children: [],
    passThrough: true, // 真实 Photoshop 新建组默认就是穿透模式
    collapsed: false,
  };

  store.dispatch(createGroupLayersCommand({ group, memberIds: ids, parentId, index: insertIndex }));
  store.setActiveLayer(groupId);
  return true;
}

/**
 * 删除一个图层，一步撤销可复原（`createRemoveLayerCommand` 自带 invert）。
 * 删的是活动图层时不用在这里手动挑替补——`AppStore.dispatch` 每次派发后都会
 * `#reconcileActive()`，活动图层消失时自动回退到原父组同下标的相邻图层
 * （§ store.ts 注释），这里重复选一遍反而可能跟它的规则打架。
 * 返回是否有修改发生（无文档/图层不存在时 false）。
 */
export function deleteLayer(store: AppStore, id: LayerId): boolean {
  const document = store.document;
  if (document === null || findLayer(document.root, id) === null) return false;
  store.dispatch(createRemoveLayerCommand(id));
  return true;
}

/**
 * 给活动图层添加一个图层蒙版（图层面板底部工具栏的蒙版图标，PS 惯例）。
 *
 * 跟真实 Photoshop 一致：有活动选区时蒙版按选区生成（选中区显示、未选中
 * 区隐藏，`writeMaskFromSelection`，结构+像素合成一条历史，同文字变形/
 * 竖排切换那套 `dispatchWithPixels` 通道）；没有选区时给一个"全部显示"的
 * 默认蒙版（`base: 255`，零分配，纯结构命令，不需要像素事务）。已经有
 * 蒙版的图层不能再加（真实 PS 这时蒙版按钮本身是禁用的），返回 false。
 */
export function addMaskToActiveLayer(store: AppStore, deps: LayerEditDeps): boolean {
  const document = store.document;
  const id = store.activeLayerId;
  if (document === null || id === null) return false;
  const layer = findLayer(document.root, id)?.layer;
  if (layer === undefined || layer.mask !== undefined) return false;

  const selection = store.selection;
  if (selection !== null && !selection.isEmpty) {
    const transaction = store.beginPixelTransaction('添加图层蒙版');
    const mask = writeMaskFromSelection(deps.tiles, selection, id, transaction);
    store.dispatchWithPixels(createAddMaskCommand(id, mask), transaction.end());
  } else {
    const maskId = maskLayerIdFor(id);
    const mask: RasterMask = {
      tiles: deps.tiles.handle(maskId),
      bounds: { x: 0, y: 0, width: document.size.width, height: document.size.height },
      linked: true,
      enabled: true,
      base: 255,
    };
    store.dispatch(createAddMaskCommand(id, mask));
  }
  return true;
}
